/**
 * HistoricoVendas.jsx
 *
 * ── Lista todas as vendas com paginação e filtro por data
 * ── Cada venda expande mostrando itens + pagamentos
 * ── Botão "Reimprimir" gera o ComprovantePDF no formato salvo pelo cliente
 * ── Toggle "Ocultar valores" mascara totais na tela (não no PDF)
 * ── Tema claro/escuro via CSS vars (mesmo padrão do projeto)
 */

import { useState, useEffect, useCallback, useMemo, memo, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import ComprovantePDF from "../venda/ComprovantePDF";
import api from "../../api/api";
import Swal from "sweetalert2";
import styles from "./HistoricoVendas.module.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatBRL = (v) =>
  Number(v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatData = (iso) => {
  // O Postgres retorna sem "Z" (ex: "2024-01-15T11:00:00"),
  // o JS trata como UTC — adicionamos "Z" para forçar UTC e deixar
  // toLocaleString converter para o fuso do navegador automaticamente.
  const str = iso && !iso.endsWith("Z") && !iso.includes("+") ? iso + "Z" : iso;
  return new Date(str).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
};

const FORMATOS_LABEL = {
  a4:     "A4",
  cupom:  "Cupom 80mm",
  bobina: "Bobina térmica",
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const PrintIcon   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const EyeIcon     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeOffIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
const SearchIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const RefreshIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>;
const UserIcon    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const ChevronIcon = ({ open }) => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>;
const WorkerIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>;
const ReceiptIcon = () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="12" y2="17"/></svg>;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <div className={styles.skeletonRow} aria-hidden="true">
    <div className={styles.skeletonBlock} style={{ width: "12%" }} />
    <div className={styles.skeletonBlock} style={{ width: "25%" }} />
    <div className={styles.skeletonBlock} style={{ width: "15%" }} />
    <div className={styles.skeletonBlock} style={{ width: "18%" }} />
    <div className={styles.skeletonBlock} style={{ width: "10%" }} />
  </div>
);

// ─── VendaCard ────────────────────────────────────────────────────────────────

const VendaCard = memo(({ venda, cliente, ocultarValores, onReimprimir }) => {
  const [expanded, setExpanded]   = useState(false);
  const [printing, setPrinting]   = useState(false);

  // Monta pagamentos: prefere tabela venda_pagamento, fallback em forma_pagamento
  const pagamentos = useMemo(() => {
    if (venda.pagamentos_detalhados?.length > 0) return venda.pagamentos_detalhados;
    // fallback: constrói array simples a partir dos campos da venda
    return [{ forma: venda.forma_pagamento, valor: Number(venda.valor_pago) }];
  }, [venda]);

  const handleReimprimir = async () => {
    setPrinting(true);
    try {
      await onReimprimir(venda, pagamentos);
    } finally {
      setPrinting(false);
    }
  };

  const valorMasked = "R$ ••••";

  return (
    <div className={styles.vendaCard}>
      {/* Header do card */}
      <div className={styles.vendaCardHeader} onClick={() => setExpanded((v) => !v)}>
        <div className={styles.vendaCardLeft}>
          <span className={styles.vendaId}>#{String(venda.id).padStart(4, "0")}</span>
          <div className={styles.vendaInfo}>
            <span className={styles.vendaData}>{formatData(venda.criado_em)}</span>
            {venda.sub_cliente_nome && (
              <span className={styles.vendaCliente}>
                <UserIcon /> {venda.sub_cliente_nome}
              </span>
            )}
            {venda.vendedor_nome && (
              <span className={styles.vendedorNome}>
                <WorkerIcon /> {venda.vendedor_nome}
              </span>
            )}
          </div>
        </div>

        <div className={styles.vendaCardRight}>
          <div className={styles.vendaValores}>
            <span className={styles.vendaForma}>{venda.forma_pagamento}</span>
            <span className={styles.vendaTotal}>
              {ocultarValores ? valorMasked : formatBRL(venda.valor_total)}
            </span>
            {Number(venda.troco) > 0 && !ocultarValores && (
              <span className={styles.vendaTroco}>
                troco {formatBRL(venda.troco)}
              </span>
            )}
          </div>
          <button
            type="button"
            className={styles.btnReimprimir}
            onClick={(e) => { e.stopPropagation(); handleReimprimir(); }}
            disabled={printing}
            title="Reimprimir comprovante"
            aria-label="Reimprimir comprovante"
          >
            {printing
              ? <span className={styles.spinner} aria-hidden="true" />
              : <PrintIcon />}
          </button>
          <span className={styles.chevron}><ChevronIcon open={expanded} /></span>
        </div>
      </div>

      {/* Conteúdo expandido */}
      {expanded && (
        <div className={styles.vendaCardBody}>
          {/* Itens */}
          <div className={styles.itensWrapper}>
            <table className={styles.itensTable}>
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Qtd</th>
                  <th>Unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(venda.itens ?? []).map((it, i) => (
                  <tr key={i}>
                    <td>{it.produto_nome ?? "—"}</td>
                    <td>{it.quantidade}</td>
                    <td>{ocultarValores ? "•••" : formatBRL(it.valor_unitario)}</td>
                    <td>
                      <strong>{ocultarValores ? "•••" : formatBRL(it.valor_total)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {Number(venda.desconto) > 0 && (
                  <tr className={styles.descontoRow}>
                    <td colSpan={3} className={styles.totalLabel}>Desconto</td>
                    <td className={styles.descontoValor}>
                      {ocultarValores ? "•••" : `− ${formatBRL(venda.desconto)}`}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className={styles.totalLabel}>Total</td>
                  <td className={styles.totalValor}>
                    {ocultarValores ? valorMasked : formatBRL(venda.valor_total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagamentos detalhados */}
          <div className={styles.pgWrapper}>
            <span className={styles.pgTitulo}>Pagamentos</span>
            <div className={styles.pgList}>
              {pagamentos.map((p, i) => (
                <div key={i} className={styles.pgItem}>
                  <span className={styles.pgForma}>{p.forma}</span>
                  <span className={styles.pgValor}>
                    {ocultarValores ? "•••" : formatBRL(p.valor)}
                  </span>
                </div>
              ))}
              {Number(venda.troco) > 0 && (
                <div className={`${styles.pgItem} ${styles.pgTroco}`}>
                  <span>Troco</span>
                  <span>{ocultarValores ? "•••" : formatBRL(venda.troco)}</span>
                </div>
              )}
            </div>
          </div>

          {venda.orcamento_id && (
            <span className={styles.orcamentoTag}>
              Gerado a partir do orçamento #{venda.orcamento_id}
            </span>
          )}
        </div>
      )}
    </div>
  );
});
VendaCard.displayName = "VendaCard";

// ─── Main ─────────────────────────────────────────────────────────────────────

const HistoricoVendas = () => {
  const { user } = useContext(AuthContext);
  const [vendas,        setVendas]        = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [ocultarValores, setOcultarValores] = useState(false);
  const [cliente,       setCliente]       = useState(null);
  const [dataInicio,    setDataInicio]    = useState("");
  const [dataFim,       setDataFim]       = useState("");
  const [busca,         setBusca]         = useState("");
  const [pagina,        setPagina]        = useState(0);

  const POR_PAGINA = 20;

  // ── Fetch cliente ──────────────────────────────────────────────────────────
  useEffect(() => {
    api.get("/clientes").then(({ data }) => setCliente(data)).catch(() => {});
  }, []);

  // ── Fetch vendas ───────────────────────────────────────────────────────────
  const fetchVendas = useCallback(async (pag = 0) => {
    setLoading(true);
    try {
      const params = {
        limit:  POR_PAGINA,
        offset: pag * POR_PAGINA,
      };
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim)    params.data_fim    = `${dataFim}T23:59:59`;

      const { data } = await api.get("/venda", { params });
      setVendas(data.vendas ?? []);
      setTotal(data.total  ?? 0);
    } catch {
      Swal.fire("Erro", "Não foi possível carregar o histórico.", "error");
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  useEffect(() => { fetchVendas(pagina); }, [fetchVendas, pagina]);

  // ── Busca local (por sub_cliente_nome ou forma_pagamento) ──────────────────
  const vendasFiltradas = useMemo(() => {
    if (!busca.trim()) return vendas;
    const t = busca.toLowerCase();
    return vendas.filter((v) =>
      v.sub_cliente_nome?.toLowerCase().includes(t) ||
      v.forma_pagamento?.toLowerCase().includes(t) ||
      String(v.id).includes(t)
    );
  }, [vendas, busca]);

  // ── Totalizadores do período carregado ─────────────────────────────────────
  const totaisPeriodo = useMemo(() => ({
    count:       vendasFiltradas.length,
    totalBruto:  vendasFiltradas.reduce((a, v) => a + Number(v.valor_total), 0),
    totalDesconto: vendasFiltradas.reduce((a, v) => a + Number(v.desconto ?? 0), 0),
  }), [vendasFiltradas]);

  // ── Reimprimir ─────────────────────────────────────────────────────────────
  const handleReimprimir = useCallback(async (venda, pagamentos) => {
    const FORMATOS = Object.fromEntries(
      Object.entries(FORMATOS_LABEL).map(([k, v]) => [k, v])
    );

    // Usa o formato configurado no cliente, ou pede
    let formato = cliente?.tipo_impressao ?? null;
    const autoImprimir = Boolean(cliente?.auto_imprimir) && formato;

    if (!autoImprimir) {
      const { value: fmt } = await Swal.fire({
        title: "Formato do comprovante",
        input: "select",
        inputOptions: FORMATOS,
        inputValue: formato ?? "a4",
        showCancelButton: true,
        confirmButtonText: "Gerar PDF ↵",
        cancelButtonText:  "Cancelar",
        allowEnterKey: true,
      });
      if (!fmt) return;
      formato = fmt;
    }

    // Monta sub-cliente a partir dos dados que o backend retorna na venda
    const subCliente = venda.sub_cliente_nome
      ? {
          nome:      venda.sub_cliente_nome,
          cpf_cnpj:  venda.sub_cliente_cpf_cnpj  ?? null,
          telefone:  venda.sub_cliente_telefone   ?? null,
          endereco:  venda.sub_cliente_endereco   ?? null,
        }
      : null;

    const vendaData = {
      estabelecimento: cliente?.nome ?? "FluxoEasy",
      itens:      (venda.itens ?? []).map((it) => ({
        produto_nome: it.produto_nome ?? "—",
        quantidade:   it.quantidade,
        valor_venda:  it.valor_unitario,
      })),
      pagamentos,
      troco:    Number(venda.troco)   || 0,
      desconto: Number(venda.desconto) || 0,
      subCliente,
      ocultarValores: false,
    };

    try {
      const blob = await pdf(
        <ComprovantePDF venda={vendaData} cliente={cliente} formato={formato} />
      ).toBlob();
      saveAs(blob, `comprovante_${formato}_venda${venda.id}.pdf`);
    } catch (err) {
      console.error("[reimprimir]", err);
      Swal.fire("Erro", "Não foi possível gerar o comprovante.", "error");
    }
  }, [cliente]);

  const totalPaginas = Math.ceil(total / POR_PAGINA);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.title}>Histórico de Vendas</h1>
          <p className={styles.subtitle}>{cliente?.nome ?? ""}</p>
        </div>

        {/* Toggle ocultar valores */}
        <button
          type="button"
          className={`${styles.btnToggleValores} ${ocultarValores ? styles.btnToggleAtivo : ""}`}
          onClick={() => setOcultarValores((v) => !v)}
          title={ocultarValores ? "Mostrar valores" : "Ocultar valores"}
        >
          {ocultarValores ? <EyeOffIcon /> : <EyeIcon />}
          {ocultarValores ? "Mostrar valores" : "Ocultar valores"}
        </button>
      </div>

      {/* ── Filtros ──────────────────────────────────────────────────────────── */}
      <div className={styles.filtrosCard}>
        <div className={styles.filtrosRow}>
          {/* Busca */}
          <div className={styles.searchWrapper}>
            <span className={styles.searchIcon}><SearchIcon /></span>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Buscar por cliente, forma de pagamento ou nº..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar vendas"
            />
          </div>

          {/* Data início */}
          <div className={styles.dateGroup}>
            <label className={styles.dateLabel}>De</label>
            <input type="date" className={styles.dateInput}
              value={dataInicio}
              onChange={(e) => { setDataInicio(e.target.value); setPagina(0); }}
            />
          </div>

          {/* Data fim */}
          <div className={styles.dateGroup}>
            <label className={styles.dateLabel}>Até</label>
            <input type="date" className={styles.dateInput}
              value={dataFim}
              onChange={(e) => { setDataFim(e.target.value); setPagina(0); }}
            />
          </div>

          {/* Limpar filtros */}
          {(dataInicio || dataFim || busca) && (
            <button type="button" className={styles.btnLimparFiltro}
              onClick={() => { setDataInicio(""); setDataFim(""); setBusca(""); setPagina(0); }}>
              Limpar
            </button>
          )}

          <button type="button" className={styles.btnRefresh}
            onClick={() => fetchVendas(pagina)} disabled={loading}
            aria-label="Atualizar">
            <RefreshIcon />
          </button>
        </div>

        {/* Totalizadores */}
        {!loading && vendasFiltradas.length > 0 && (
          <div className={styles.totaisRow}>
            <span className={styles.totalItem}>
              <strong>{totaisPeriodo.count}</strong> venda{totaisPeriodo.count !== 1 ? "s" : ""}
            </span>
            <span className={styles.totalItem}>
              Total:{" "}
              <strong>
                {ocultarValores ? "R$ ••••" : formatBRL(totaisPeriodo.totalBruto)}
              </strong>
            </span>
            {totaisPeriodo.totalDesconto > 0 && (
              <span className={`${styles.totalItem} ${styles.totalDesconto}`}>
                Descontos:{" "}
                <strong>
                  {ocultarValores ? "•••" : `− ${formatBRL(totaisPeriodo.totalDesconto)}`}
                </strong>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Lista ────────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className={styles.listaWrapper}>
          {[1,2,3,4,5].map((n) => <SkeletonRow key={n} />)}
        </div>
      ) : vendasFiltradas.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><ReceiptIcon /></div>
          <p className={styles.emptyTitle}>
            {busca || dataInicio || dataFim ? "Nenhuma venda encontrada" : "Nenhuma venda registrada"}
          </p>
          <p className={styles.emptyDesc}>
            {busca || dataInicio || dataFim
              ? "Tente ajustar os filtros."
              : "As vendas aparecerão aqui após a primeira finalização."}
          </p>
        </div>
      ) : (
        <div className={styles.listaWrapper}>
          {vendasFiltradas.map((venda) => (
            <VendaCard
              key={venda.id}
              venda={venda}
              cliente={cliente}
              ocultarValores={ocultarValores}
              onReimprimir={handleReimprimir}
            />
          ))}
        </div>
      )}

      {/* ── Paginação ──────────────────────────────────────────────────────── */}
      {totalPaginas > 1 && (
        <div className={styles.paginacao}>
          <button type="button" className={styles.pgBtn}
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
            disabled={pagina === 0 || loading}>
            ← Anterior
          </button>
          <span className={styles.pgInfo}>
            Página {pagina + 1} de {totalPaginas}
          </span>
          <button type="button" className={styles.pgBtn}
            onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}
            disabled={pagina >= totalPaginas - 1 || loading}>
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoricoVendas;